import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { ApiConfig, ScApiService } from '@sc-ui';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DemoService } from './demo.service';

// Replaces ScApiService app-wide (see app.module). In demo mode GETs are served from
// assets/demo-api/<path>[/<query>].json and writes never reach the backend. Login and
// registration bypass get/post in the base class, so they stay live.
@Injectable()
export class DemoApiService extends ScApiService {

  constructor(
    config: ApiConfig,
    private assets: HttpClient,
    router: Router,
    @Inject(PLATFORM_ID) platformId: string,
    private demo: DemoService) {

    super(config, assets, router, platformId);
  }

  get<T>(res, host = undefined): Observable<T> {

    if (!this.isDemoRequest(res, host)) return super.get<T>(res, host);

    // the exact query first, then the query-less payload of the same route
    var [path, query] = res.split('?');
    var files = query ? [path + '/' + query, path] : [path];

    return this.getFirstExisting<T>(files.map(f => 'assets/demo-api/' + f + '.json'))
      .pipe(map(d => this.toAbsoluteAssetUris(d)));
  }

  post(res, data, host = undefined) {
    if (this.isDemoRequest(res, host)) return of(data ?? {});
    return super.post(res, data, host);
  }

  put(res, data, host = undefined) {
    if (this.isDemoRequest(res, host)) return of(data ?? {});
    return super.put(res, data, host);
  }

  delete(res, host = undefined) {
    if (this.isDemoRequest(res, host)) return of({});
    return super.delete(res, host);
  }

  private isDemoRequest(res: string, host: string) {
    return this.demo.isActive && !host && !res.startsWith('https://');
  }

  private getFirstExisting<T>(uris: string[]): Observable<T> {

    var [uri, ...fallbacks] = uris;

    return this.assets.get<T>(uri).pipe(catchError(error => {
      if (fallbacks.length) return this.getFirstExisting<T>(fallbacks);
      console.warn('Demo mode: no payload at ' + uri);
      return throwError(() => error);
    }));
  }

  // sc-avatar's imageSize pipe parses images as absolute urls
  private toAbsoluteAssetUris(value: any): any {
    if (typeof value === 'string') return value.startsWith('/assets/') ? window.location.origin + value : value;
    if (Array.isArray(value)) return value.map(v => this.toAbsoluteAssetUris(v));
    if (value && typeof value === 'object') {
      for (var key in value) value[key] = this.toAbsoluteAssetUris(value[key]);
    }
    return value;
  }
}
